/**
 * collection-form-specs (US-AIF.P1-0248 · @UC9420 @UC9421) — FORM COL-OPEN-AMOUNT + COL-TARGET-SPLIT.
 *
 * MỞ RỘNG mẫu COL-EQUAL (col-equal-form.ts) sang 2 usecase thu nữa, CÙNG engine collection (createWithMode):
 *  - COL-OPEN-AMOUNT (mode `open_amount`): thu TỰ NGUYỆN — mỗi người đóng tuỳ tâm. KHÔNG field money
 *    (số thực đóng khai lúc record-paid). Chuỗi field: [fund_picker(fundId) → text(title) → date(dueDate)].
 *  - COL-TARGET-SPLIT (mode `target_split`): đặt TỔNG mục tiêu, engine chia đầu người. money value là
 *    TỔNG (targetTotalMinor), không phải per-member. Chuỗi field: [fund_picker(fundId) → text(title) →
 *    money(targetTotalMinor) → date(dueDate)].
 *
 * HÀM THUẦN (DP-3): KHÔNG DB, KHÔNG LLM, KHÔNG Date.now (trừ `now` inject cho test date-not-past).
 * Money = string minor VND (B1 — no-float, >0). fund_picker options do SERVICE nạp (quỹ THẬT của user)
 * — domain chỉ validate value ∈ options (chống IDOR quỹ lạ ở LỚP domain, ngoài RBAC service).
 *
 * REUSE THUẦN: cùng guard money/title/date với COL-EQUAL (assertAdhocAmountPositive/assertDueDateNotPast +
 * trần BIGINT + trần title). KHÔNG nhân bản công thức tiền — chỉ khác CHUỖI FIELD + amountMode dispatch.
 */
import { DomainError } from "../errors.js";
import {
  assertAdhocAmountPositive,
  assertDueDateNotPast,
} from "../adhoc-collection.js";
import {
  assertStartDateNotPast,
  DEFAULT_GROUP_TIMEZONE,
  type CollectionFrequency,
} from "../collection-schedule.js";
import { validateFieldValue, type SlotFieldSpec, type SlotOption, type FormState } from "./form-slot-filling.js";

/** intentCode COL-OPEN-AMOUNT (khớp FORM_SPEC_CATALOG + capability-catalog + router). */
export const COL_OPEN_AMOUNT_INTENT_CODE = "COL-OPEN-AMOUNT";
/** intentCode COL-TARGET-SPLIT. */
export const COL_TARGET_SPLIT_INTENT_CODE = "COL-TARGET-SPLIT";
/** intentCode COL-RECURRING (US-AIF.P1-0251 · @UC9425) — thu định kỳ (createSchedule). */
export const COL_RECURRING_INTENT_CODE = "COL-RECURRING";
/** intentCode COL-DEADLINE (US-AIF.P1-0251 · @UC9426) — thu có hạn chót → nợ (createAdhoc + dueDate). */
export const COL_DEADLINE_INTENT_CODE = "COL-DEADLINE";

/** specVersion 2 form mới (tăng khi đổi chuỗi field — session lưu để phát hiện drift). */
export const COL_OPEN_AMOUNT_SPEC_VERSION = 1;
export const COL_TARGET_SPLIT_SPEC_VERSION = 1;
export const COL_RECURRING_SPEC_VERSION = 1;
export const COL_DEADLINE_SPEC_VERSION = 1;

/**
 * 🔴 Tần suất ĐƯỢC PHÉP cho AI form COL-RECURRING (US-AIF.P1-0251 · index §6). Phủ 3/4 giá trị của
 * `CollectionFrequency` (`COLLECTION_FREQUENCIES`): monthly/quarterly/yearly. `weekly` bị LOẠI KHỎI AI
 * form CƠ BẢN vì `buildRecurrence("weekly")` yêu cầu THÊM field `weekday` (dependent field — chọn weekly
 * ⇒ hiện dropdown thứ). Field phụ thuộc để Phase sau (index §6 chốt: giữ frequency cơ bản, KHÔNG thêm
 * field mới ở task này). Nhãn tiếng Việt server điền (KHÔNG do LLM bịa) cho renderer single_choice.
 *
 * ⚠️ ĐỒNG NGUỒN: value PHẢI ⊆ `COLLECTION_FREQUENCIES` (@goppy/domain) — thêm/bớt tần suất phải giữ khớp.
 * Test khoá bất biến này (mọi value ∈ enum + weekly bị loại).
 */
export const COL_RECURRING_FREQUENCY_LABELS_VI: Readonly<Record<string, string>> = Object.freeze({
  monthly: "Hàng tháng",
  quarterly: "Hàng quý",
  yearly: "Hàng năm",
});

/** Danh sách tần suất (value) AI form hỗ trợ — thứ tự hiển thị options single_choice (tất-định). */
export const COL_RECURRING_FREQUENCIES: readonly CollectionFrequency[] = Object.freeze([
  "monthly",
  "quarterly",
  "yearly",
]);

/** options single_choice(frequency) cho COL-RECURRING (server nạp — value ∈ enum). Immutable copy. */
export function buildRecurringFrequencyOptions(): SlotOption[] {
  return COL_RECURRING_FREQUENCIES.map((value) => ({ value }));
}

/**
 * 🔴 Trần PG signed BIGINT + trần độ dài tiêu đề — NHÂN BẢN y hệt COL_EQUAL_PG_BIGINT_MAX /
 * COL_EQUAL_TITLE_MAX_LENGTH (col-equal-form.ts) + contracts ADHOC_PG_BIGINT_MAX / ADHOC_TITLE_MAX_LENGTH.
 * Domain thuần KHÔNG dep @goppy/contracts ⇒ giữ literal chung ở đây; đổi một bên PHẢI đổi mọi bên.
 */
export const COLLECTION_FORM_PG_BIGINT_MAX = 9223372036854775807n;
export const COLLECTION_FORM_TITLE_MAX_LENGTH = 100;

/** Thứ tự field COL-OPEN-AMOUNT (tất-định — KHÔNG money vì tự nguyện). */
export const COL_OPEN_AMOUNT_FIELD_ORDER = ["fundId", "title", "dueDate"] as const;

/** Thứ tự field COL-TARGET-SPLIT (tất-định — money = TỔNG mục tiêu). */
export const COL_TARGET_SPLIT_FIELD_ORDER = [
  "fundId",
  "title",
  "targetTotalMinor",
  "dueDate",
] as const;

/**
 * Thứ tự field COL-RECURRING (tất-định — index §1): fund → tên lịch → tiền/người → tần suất → ngày bắt
 * đầu. money = amountPerMember (per-member). frequency single_choice ∈ COL_RECURRING_FREQUENCIES.
 */
export const COL_RECURRING_FIELD_ORDER = [
  "fundId",
  "title",
  "amountPerMember",
  "frequency",
  "startDate",
] as const;

/**
 * Thứ tự field COL-DEADLINE (tất-định — index §1): giống COL-EQUAL nhưng NHẤN dueDate (hạn chót → nợ).
 * money = amountPerMember. dueDate = hạn chót (quá hạn → ghi nợ do lifecycle ledger/settlement hiện hữu).
 */
export const COL_DEADLINE_FIELD_ORDER = [
  "fundId",
  "title",
  "amountPerMember",
  "dueDate",
] as const;

/** Payload COL-OPEN-AMOUNT đã validate — nối vào createWithMode(amountMode='open_amount'). KHÔNG có tiền. */
export interface ColOpenAmountPayload {
  readonly fundId: string;
  readonly title: string;
  /** 'YYYY-MM-DD'. */
  readonly dueDate: string;
}

/** Payload COL-TARGET-SPLIT đã validate — nối vào createWithMode(amountMode='target_split'). */
export interface ColTargetSplitPayload {
  readonly fundId: string;
  readonly title: string;
  /** STRING số nguyên dương minor — TỔNG mục tiêu (engine chia đầu người). */
  readonly targetTotalMinor: string;
  /** 'YYYY-MM-DD'. */
  readonly dueDate: string;
}

/** Payload COL-RECURRING đã validate — nối THẲNG vào CreateScheduleInput (createSchedule). */
export interface ColRecurringPayload {
  readonly fundId: string;
  /** name của CollectionSchedule (map từ field title). */
  readonly name: string;
  /** STRING số nguyên dương minor per-member (B1 — service parse → BigInt). */
  readonly amountPerMemberMinor: string;
  /** Tần suất ∈ COL_RECURRING_FREQUENCIES (⊆ CollectionFrequency). */
  readonly frequency: CollectionFrequency;
  /** 'YYYY-MM-DD' — ngày bắt đầu (KHÔNG quá khứ). */
  readonly startDate: string;
}

/** Payload COL-DEADLINE đã validate — nối vào CreateAdhocInput (createAdhoc, dueDate = hạn chót). */
export interface ColDeadlinePayload {
  readonly fundId: string;
  readonly title: string;
  /** STRING số nguyên dương minor per-member (B1). */
  readonly amountMinor: string;
  /** 'YYYY-MM-DD' — hạn chót (quá hạn → nợ do lifecycle hiện hữu). */
  readonly dueDate: string;
}

/**
 * Dựng chuỗi field COL-OPEN-AMOUNT (fund_picker → text → date). MỌI field required. KHÔNG money field —
 * thu tự nguyện, số thực đóng ghi lúc record-paid. Immutable: copy fundOptions sang mảng options mới.
 */
export function buildColOpenAmountFields(
  fundOptions: readonly SlotOption[],
): SlotFieldSpec[] {
  return [
    { key: "fundId", type: "fund_picker", required: true, options: [...fundOptions] },
    { key: "title", type: "text", required: true },
    { key: "dueDate", type: "date", required: true },
  ];
}

/**
 * Dựng chuỗi field COL-TARGET-SPLIT (fund_picker → text → money → date). money = TỔNG mục tiêu.
 * Cùng SHAPE COL-EQUAL nhưng ngữ nghĩa money khác (tổng thay vì per-member). Immutable copy options.
 */
export function buildColTargetSplitFields(
  fundOptions: readonly SlotOption[],
): SlotFieldSpec[] {
  return [
    { key: "fundId", type: "fund_picker", required: true, options: [...fundOptions] },
    { key: "title", type: "text", required: true },
    { key: "targetTotalMinor", type: "money", required: true },
    { key: "dueDate", type: "date", required: true },
  ];
}

/**
 * Dựng chuỗi field COL-RECURRING (fund_picker → text → money(per-member) → single_choice(frequency) →
 * date(startDate)). frequency options = COL_RECURRING_FREQUENCIES (server nạp — value ∈ enum, weekly bị
 * loại; xem COL_RECURRING_FREQUENCY_LABELS_VI). MỌI field required. Immutable copy options.
 */
export function buildColRecurringFields(
  fundOptions: readonly SlotOption[],
): SlotFieldSpec[] {
  return [
    { key: "fundId", type: "fund_picker", required: true, options: [...fundOptions] },
    { key: "title", type: "text", required: true },
    { key: "amountPerMember", type: "money", required: true },
    {
      key: "frequency",
      type: "single_choice",
      required: true,
      options: buildRecurringFrequencyOptions(),
    },
    { key: "startDate", type: "date", required: true },
  ];
}

/**
 * Dựng chuỗi field COL-DEADLINE (fund_picker → text → money(per-member) → date(dueDate)). CÙNG SHAPE
 * COL-EQUAL nhưng ngữ nghĩa dueDate = hạn chót (quá hạn → nợ). Immutable copy options.
 */
export function buildColDeadlineFields(
  fundOptions: readonly SlotOption[],
): SlotFieldSpec[] {
  return [
    { key: "fundId", type: "fund_picker", required: true, options: [...fundOptions] },
    { key: "title", type: "text", required: true },
    { key: "amountPerMember", type: "money", required: true },
    { key: "dueDate", type: "date", required: true },
  ];
}

/** Ném DomainError REQUIRED_FIELD_MISSING cho field bắt buộc còn trống (mirror col-equal-form). */
function assertPresent(value: string | null | undefined, field: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `Thiếu thông tin bắt buộc: ${field}.`,
    );
  }
  return value.trim();
}

/** fundId ∈ options (domain-level IDOR guard — quỹ ngoài danh sách user ⇒ reject). */
function validateFundId(
  formState: FormState,
  fundOptions: readonly SlotOption[],
): string {
  const fundIdRaw = assertPresent(formState.fundId, "fundId");
  const allowedFundIds = new Set(fundOptions.map((o) => o.value));
  if (!allowedFundIds.has(fundIdRaw)) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      "Quỹ được chọn không nằm trong danh sách quỹ của bạn.",
    );
  }
  return fundIdRaw;
}

/** title non-empty + ≤ trần độ dài (H1 — mirror HTTP .max(100)). */
function validateTitle(formState: FormState): string {
  const title = assertPresent(formState.title, "title");
  if (title.length > COLLECTION_FORM_TITLE_MAX_LENGTH) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `Tiêu đề quá dài (tối đa ${COLLECTION_FORM_TITLE_MAX_LENGTH} ký tự).`,
    );
  }
  return title;
}

/** dueDate ISO hợp lệ + KHÔNG quá khứ (tz group). */
function validateDueDate(formState: FormState, now: Date): string {
  const dueDateRaw = assertPresent(formState.dueDate, "dueDate");
  const dateValidation = validateFieldValue(
    { key: "dueDate", type: "date", required: true },
    dueDateRaw,
  );
  if (!dateValidation.ok) {
    throw new DomainError("COLLECTION_DUE_DATE_IN_PAST", dateValidation.message);
  }
  assertDueDateNotPast(parseIsoDateUtc(dateValidation.value), DEFAULT_GROUP_TIMEZONE, now);
  return dateValidation.value;
}

/** startDate ISO hợp lệ + KHÔNG quá khứ (tz group) — dùng chung guard với schedule (assertStartDateNotPast). */
function validateStartDate(formState: FormState, now: Date): string {
  const startDateRaw = assertPresent(formState.startDate, "startDate");
  const dateValidation = validateFieldValue(
    { key: "startDate", type: "date", required: true },
    startDateRaw,
  );
  if (!dateValidation.ok) {
    throw new DomainError("COLLECTION_START_DATE_IN_PAST", dateValidation.message);
  }
  // Reuse domain guard (assertStartDateNotPast ném COLLECTION_START_DATE_IN_PAST) — KHÔNG nhân bản.
  assertStartDateNotPast(parseIsoDateUtc(dateValidation.value), DEFAULT_GROUP_TIMEZONE, now);
  return dateValidation.value;
}

/** frequency ∈ COL_RECURRING_FREQUENCIES (options-động single_choice) — else DomainError (map 400). */
function validateFrequency(formState: FormState): CollectionFrequency {
  const raw = assertPresent(formState.frequency, "frequency");
  const validation = validateFieldValue(
    {
      key: "frequency",
      type: "single_choice",
      required: true,
      options: buildRecurringFrequencyOptions(),
    },
    raw,
  );
  if (!validation.ok) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      "Tần suất thu không nằm trong danh sách cho phép.",
    );
  }
  // value đã ∈ options ⇒ ∈ COL_RECURRING_FREQUENCIES ⊆ CollectionFrequency (an toàn ép kiểu).
  return validation.value as CollectionFrequency;
}

/**
 * money minor no-float + >0 (B1) + ≤ trần BIGINT (C3). Dùng chung cho amountPerMember (COL-EQUAL) và
 * targetTotalMinor (COL-TARGET-SPLIT) — key truyền vào để thông điệp lỗi rõ field nào.
 */
function validateMoneyMinor(formState: FormState, key: string): string {
  const raw = assertPresent(formState[key], key);
  const validation = validateFieldValue({ key, type: "money", required: true }, raw);
  if (!validation.ok) {
    throw new DomainError("COLLECTION_AMOUNT_NOT_POSITIVE", validation.message);
  }
  // >0 (0/âm đã qua regex minor? "0" hợp regex → chốt ở đây).
  assertAdhocAmountPositive(BigInt(validation.value));
  // C3 — chốt TRẦN BIGINT (lọt regex minor 19 số nhưng vượt PG BIGINT ⇒ P2020→500 nếu không chốt).
  if (BigInt(validation.value) > COLLECTION_FORM_PG_BIGINT_MAX) {
    throw new DomainError(
      "COLLECTION_AMOUNT_NOT_POSITIVE",
      "Số tiền vượt quá giới hạn cho phép.",
    );
  }
  return validation.value;
}

/**
 * Validate CẢ BỘ formState COL-OPEN-AMOUNT → ColOpenAmountPayload (fail-fast). KHÔNG money (tự nguyện).
 * Thứ tự chốt: fundId ∈ options → title non-empty ≤100 → dueDate ISO không quá khứ.
 */
export function validateColOpenAmountForm(
  formState: FormState,
  fundOptions: readonly SlotOption[],
  now: Date = new Date(),
): ColOpenAmountPayload {
  const fundId = validateFundId(formState, fundOptions);
  const title = validateTitle(formState);
  const dueDate = validateDueDate(formState, now);
  return { fundId, title, dueDate };
}

/**
 * Validate CẢ BỘ formState COL-TARGET-SPLIT → ColTargetSplitPayload (fail-fast).
 * Thứ tự chốt: fundId ∈ options → title non-empty ≤100 → targetTotalMinor minor >0 no-float ≤ trần
 * BIGINT → dueDate ISO không quá khứ.
 */
export function validateColTargetSplitForm(
  formState: FormState,
  fundOptions: readonly SlotOption[],
  now: Date = new Date(),
): ColTargetSplitPayload {
  const fundId = validateFundId(formState, fundOptions);
  const title = validateTitle(formState);
  const targetTotalMinor = validateMoneyMinor(formState, "targetTotalMinor");
  const dueDate = validateDueDate(formState, now);
  return { fundId, title, targetTotalMinor, dueDate };
}

/**
 * Validate CẢ BỘ formState COL-RECURRING → ColRecurringPayload (fail-fast). Thứ tự chốt: fundId ∈ options
 * → title (=name) non-empty ≤100 → amountPerMember minor >0 no-float ≤ trần BIGINT → frequency ∈ enum →
 * startDate ISO không quá khứ. Payload nối THẲNG vào createSchedule (name/amountPerMemberMinor/frequency/
 * startDate). `now` inject cho test date-not-past.
 */
export function validateColRecurringForm(
  formState: FormState,
  fundOptions: readonly SlotOption[],
  now: Date = new Date(),
): ColRecurringPayload {
  const fundId = validateFundId(formState, fundOptions);
  const name = validateTitle(formState);
  const amountPerMemberMinor = validateMoneyMinor(formState, "amountPerMember");
  const frequency = validateFrequency(formState);
  const startDate = validateStartDate(formState, now);
  return { fundId, name, amountPerMemberMinor, frequency, startDate };
}

/**
 * Validate CẢ BỘ formState COL-DEADLINE → ColDeadlinePayload (fail-fast). ĐỒNG guard với COL-EQUAL
 * (fundId ∈ options, title ≤100, amount minor >0 no-float ≤ trần BIGINT, dueDate ISO không quá khứ) —
 * chỉ khác NGỮ NGHĨA dueDate = hạn chót (quá hạn → nợ). Payload nối vào createAdhoc.
 */
export function validateColDeadlineForm(
  formState: FormState,
  fundOptions: readonly SlotOption[],
  now: Date = new Date(),
): ColDeadlinePayload {
  const fundId = validateFundId(formState, fundOptions);
  const title = validateTitle(formState);
  const amountMinor = validateMoneyMinor(formState, "amountPerMember");
  const dueDate = validateDueDate(formState, now);
  return { fundId, title, amountMinor, dueDate };
}

/** Parse 'YYYY-MM-DD' (đã validate ISO) → Date UTC midnight (ổn định, không phụ thuộc tz máy). */
function parseIsoDateUtc(isoDate: string): Date {
  const [y, m, d] = isoDate.split("-").map((p) => Number.parseInt(p, 10));
  return new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1));
}
